// NextScene — cinematic dark login modal.
// Extracted from Landing.jsx so any page can trigger it via useLoginPrompt().
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { openGoogleRedirect } from "@/lib/googleAuth";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { X, Loader2, Smartphone } from "lucide-react";

export default function LoginModal({ open, onClose }) {
  const nav = useNavigate();
  const { setUser } = useAuth() || {};
  const [view, setView] = useState("choose"); // choose | mobile-phone | mobile-otp
  const [busy, setBusy] = useState(false);
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [otpHint, setOtpHint] = useState("");

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);
  useEffect(() => {
    if (!open) { setView("choose"); setPhone(""); setOtp(""); setOtpHint(""); }
  }, [open]);

  if (!open) return null;

  const goGoogle = () => openGoogleRedirect({ redirectTo: window.location.pathname === "/" ? "/app" : window.location.pathname });
  const soonToast = (name) => toast.info(`${name} sign-in coming soon`);

  const sendOtp = async () => {
    if (phone.length !== 10) return toast.error("Enter your 10-digit mobile number");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/phone/send-otp", { phone_number: `+91${phone}` });
      setView("mobile-otp");
      setOtpHint(data.hint || "");
      toast.success(data.hint ? "OTP sent (MOCK)" : "OTP sent to your phone");
    } catch (err) { toast.error(errMsg(err, "Could not send OTP")); }
    finally { setBusy(false); }
  };

  const verifyOtp = async () => {
    if (otp.length !== 6) return toast.error("Enter the 6-digit OTP");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/phone/verify-otp", {
        phone_number: `+91${phone}`, code: otp, terms_accepted: true,
      });
      localStorage.setItem("ns_token", data.token);
      setUser && setUser(data.user);
      toast.success(data.is_new ? "Welcome to NextScene!" : "Welcome back!");
      onClose();
      // For public pages, stay put so the guarded action can retry. For the
      // landing page, hand-off to the authed shell.
      if (window.location.pathname === "/") {
        nav(data.needs_onboarding ? "/app/onboarding" : "/app");
      } else if (data.needs_onboarding) {
        nav("/app/onboarding");
      }
    } catch (err) { toast.error(errMsg(err, "Invalid OTP")); }
    finally { setBusy(false); }
  };

  const providerBtnStyle = {
    width: "100%", height: "52px", borderRadius: "16px",
    background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)",
    color: "#F5F3EE", fontFamily: "'Inter', 'SF Pro Display', system-ui, sans-serif",
    fontWeight: 500, fontSize: "15px",
    display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "12px",
    position: "relative", cursor: "pointer",
  };
  const iconLeft = { position: "absolute", left: "18px", top: "50%", transform: "translateY(-50%)", display: "inline-flex" };

  return (
    <div
      data-testid="login-modal-backdrop"
      onClick={onClose}
      className="fixed inset-0 z-[100] flex items-center justify-center"
      style={{ background: "rgba(0,0,0,0.68)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)" }}
    >
      <div
        data-testid="login-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="login-modal-title"
        className="relative"
        style={{
          width: "min(420px, calc(100vw - 32px))",
          background: "#0B0B0D",
          border: "1px solid rgba(199,154,43,0.18)",
          borderRadius: "28px",
          padding: "32px",
          boxShadow: "0 30px 80px rgba(0,0,0,0.65), 0 0 40px rgba(199,154,43,0.10)",
        }}
      >
        <button
          type="button"
          onClick={onClose}
          data-testid="login-modal-close"
          aria-label="Close login modal"
          className="absolute inline-flex items-center justify-center"
          style={{ top: "18px", right: "18px", width: "32px", height: "32px", background: "transparent", border: "none", color: "rgba(255,255,255,0.72)", cursor: "pointer" }}
        >
          <X size={20} />
        </button>

        {view === "choose" && (
          <>
            <h2 id="login-modal-title" className="text-center" style={{ margin: 0, fontFamily: "'Ogg', 'Ogg Regular', 'Didot', 'Bodoni Moda', 'Playfair Display', Georgia, serif", fontWeight: 400, color: "#F5F3EE", fontSize: "34px", lineHeight: 1.1, letterSpacing: "-0.5px" }}>
              Welcome to <span style={{ color: "#C79A2B" }}>nextscene</span>
            </h2>
            <p className="text-center" style={{ marginTop: "6px", fontFamily: "'Inter', 'SF Pro Display', system-ui, sans-serif", fontSize: "15px", color: "rgba(255,255,255,0.72)", fontWeight: 400 }}>
              Continue your journey
            </p>

            <div className="flex flex-col" style={{ marginTop: "24px", gap: "12px" }}>
              <button type="button" data-testid="login-provider-google" className="ns-login-provider" style={providerBtnStyle} onClick={goGoogle}>
                <span style={iconLeft}>
                  <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
                    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                  </svg>
                </span>
                Continue with Google
              </button>

              <button type="button" data-testid="login-provider-apple" className="ns-login-provider" style={providerBtnStyle} onClick={() => soonToast("Apple")}>
                <span style={iconLeft}>
                  <svg width="20" height="20" viewBox="0 0 384 512" aria-hidden="true">
                    <path fill="#F5F3EE" d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zM259.2 108.1c22.7-27 20.6-51.6 19.9-60.5-20 1.2-43.1 13.6-56.3 28.9-14.5 16.4-23 36.7-21.2 58.9 21.6 1.7 41.3-9.4 57.6-27.3z"/>
                  </svg>
                </span>
                Continue with Apple
              </button>

              <button type="button" data-testid="login-provider-facebook" className="ns-login-provider" style={providerBtnStyle} onClick={() => soonToast("Facebook")}>
                <span style={iconLeft}>
                  <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
                    <path fill="#1877F2" d="M24 12.073C24 5.446 18.627.073 12 .073S0 5.446 0 12.073c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.469h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.469h-2.796v8.385C19.612 23.027 24 18.063 24 12.073z"/>
                  </svg>
                </span>
                Continue with Facebook
              </button>

              <button type="button" data-testid="login-provider-mobile" className="ns-login-provider" style={providerBtnStyle} onClick={() => setView("mobile-phone")}>
                <span style={iconLeft}><Smartphone size={20} color="#C79A2B" /></span>
                Continue with Mobile Number
              </button>
            </div>

            <div className="flex items-center" style={{ marginTop: "20px", marginBottom: "12px", gap: "12px" }}>
              <div style={{ flex: 1, height: "1px", background: "rgba(255,255,255,0.08)" }} />
              <span style={{ fontFamily: "'Inter'", fontSize: "13px", color: "rgba(255,255,255,0.6)" }}>or</span>
              <div style={{ flex: 1, height: "1px", background: "rgba(255,255,255,0.08)" }} />
            </div>

            <p className="text-center" style={{ margin: 0, fontFamily: "'Inter'", fontSize: "13px", color: "rgba(255,255,255,0.68)" }}>
              Already have an account?{" "}
              <button type="button" onClick={() => setView("mobile-phone")} data-testid="login-modal-signin-link"
                style={{ background: "transparent", border: "none", color: "#C79A2B", cursor: "pointer", padding: 0, fontFamily: "inherit", fontSize: "inherit", fontWeight: 500 }}
                className="hover:underline">
                Sign In
              </button>
            </p>
          </>
        )}

        {view === "mobile-phone" && (
          <>
            <h2 className="text-center" style={{ margin: 0, fontFamily: "'Ogg', 'Ogg Regular', 'Didot', 'Playfair Display', Georgia, serif", fontWeight: 400, color: "#F5F3EE", fontSize: "28px", lineHeight: 1.15 }}>
              Enter your mobile
            </h2>
            <p className="text-center" style={{ marginTop: "6px", fontFamily: "'Inter'", fontSize: "14px", color: "rgba(255,255,255,0.72)" }}>
              We'll send you a 6-digit OTP
            </p>
            <div className="flex items-center" style={{ marginTop: "22px", gap: "10px" }}>
              <div style={{ height: "52px", padding: "0 14px", display: "inline-flex", alignItems: "center", borderRadius: "12px", border: "1px solid rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.03)", color: "#F5F3EE", fontFamily: "'Inter'", fontSize: "15px" }}>+91</div>
              <input type="tel" inputMode="numeric" maxLength={10} value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))} placeholder="10-digit mobile" data-testid="login-modal-phone" autoFocus
                style={{ flex: 1, height: "52px", padding: "0 14px", borderRadius: "12px", border: "1px solid rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.03)", color: "#F5F3EE", fontFamily: "'Inter'", fontSize: "15px", outline: "none" }}
                onKeyDown={(e) => { if (e.key === "Enter") sendOtp(); }} />
            </div>
            <button type="button" onClick={sendOtp} disabled={busy || phone.length !== 10} data-testid="login-modal-send-otp" className="final-cta-btn"
              style={{ width: "100%", marginTop: "18px", height: "52px", borderRadius: "999px",
                background: phone.length === 10 && !busy ? "#C79A2B" : "rgba(199,154,43,0.35)",
                color: "#070709", fontFamily: "'Inter'", fontWeight: 600, fontSize: "15px",
                border: "none", cursor: phone.length === 10 && !busy ? "pointer" : "not-allowed",
                display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
              {busy ? <Loader2 size={18} className="animate-spin"/> : null}
              Send OTP
            </button>
            <button type="button" onClick={() => setView("choose")} data-testid="login-modal-back"
              style={{ width: "100%", marginTop: "12px", background: "transparent", border: "none", color: "rgba(255,255,255,0.68)", fontFamily: "'Inter'", fontSize: "13px", cursor: "pointer" }}
              className="hover:underline">
              ← Back to sign-in options
            </button>
          </>
        )}

        {view === "mobile-otp" && (
          <>
            <h2 className="text-center" style={{ margin: 0, fontFamily: "'Ogg', 'Ogg Regular', 'Didot', 'Playfair Display', Georgia, serif", fontWeight: 400, color: "#F5F3EE", fontSize: "28px", lineHeight: 1.15 }}>
              Enter OTP
            </h2>
            <p className="text-center" style={{ marginTop: "6px", fontFamily: "'Inter'", fontSize: "14px", color: "rgba(255,255,255,0.72)" }}>
              Code sent to +91 {phone}
            </p>
            {otpHint && (
              <p className="text-center" style={{ marginTop: "6px", fontFamily: "'Inter'", fontSize: "12px", color: "#F4C550" }}>
                MOCK: use {otpHint.replace(/^MOCK OTP:\s*/, "")}
              </p>
            )}
            <input type="tel" inputMode="numeric" maxLength={6} value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} placeholder="6-digit code" data-testid="login-modal-otp" autoFocus
              style={{ width: "100%", marginTop: "22px", height: "56px", padding: "0 16px",
                borderRadius: "12px", border: "1px solid rgba(255,255,255,0.08)",
                background: "rgba(255,255,255,0.03)", color: "#F5F3EE",
                fontFamily: "'Inter'", fontSize: "20px", letterSpacing: "0.35em",
                textAlign: "center", outline: "none" }}
              onKeyDown={(e) => { if (e.key === "Enter") verifyOtp(); }} />
            <button type="button" onClick={verifyOtp} disabled={busy || otp.length !== 6} data-testid="login-modal-verify-otp" className="final-cta-btn"
              style={{ width: "100%", marginTop: "18px", height: "52px", borderRadius: "999px",
                background: otp.length === 6 && !busy ? "#C79A2B" : "rgba(199,154,43,0.35)",
                color: "#070709", fontFamily: "'Inter'", fontWeight: 600, fontSize: "15px",
                border: "none", cursor: otp.length === 6 && !busy ? "pointer" : "not-allowed",
                display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
              {busy ? <Loader2 size={18} className="animate-spin"/> : null}
              Verify &amp; Continue
            </button>
            <button type="button" onClick={() => { setView("mobile-phone"); setOtp(""); }} data-testid="login-modal-change-phone"
              style={{ width: "100%", marginTop: "12px", background: "transparent", border: "none", color: "rgba(255,255,255,0.68)", fontFamily: "'Inter'", fontSize: "13px", cursor: "pointer" }}
              className="hover:underline">
              ← Change mobile number
            </button>
          </>
        )}
      </div>
    </div>
  );
}
